import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Plus, Share2, Play, History, LayoutGrid, List, Settings, ChevronDown } from 'lucide-react';

import Memory from './Memory';
import AddMemory from './AddMemory';
import ImageLightbox from './ImageLightbox';
import Modal from './Modal';
import { SiteFooter, ProfileAvatar } from './SiteLayout';
import { initials, normalizeMemory, normalizeProfile, profilePath, profileDates, ROMAN_SLUG } from './memoryUtils';
import './MemorialPage.css';

const PAGE_SIZE = 12;
const ProfileProjection = lazy(() => import('./ProfileProjection'));
const FILTERS = [
  ['all', 'Vše'],
  ['image', 'Fotografie'],
  ['story', 'Příběhy'],
  ['quotation', 'Citáty'],
  ['videos', 'Videa'],
];
export const countLabel = (count, one, few, many) => `${count} ${count === 1 ? one : count >= 2 && count <= 4 ? few : many}`;

function MemoryStream({ profile, type, view, onImageClick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const cursor = useRef(0);
  const busy = useRef(false);
  const active = useRef(true);
  const request = useRef(null);
  const sentinel = useRef(null);
  const loadMore = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    setError('');
    request.current = new AbortController();
    try {
      const params = new URLSearchParams({ profile_id: profile.id, count: PAGE_SIZE, start_id: cursor.current });
      if (type !== 'all') params.set('type', type);
      const response = await fetch(`/api/memories?${params}`, { signal: request.current.signal });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (!active.current) return;
      setRows((previous) => [...previous, ...data.map(normalizeMemory)]);
      if (data.length) cursor.current = data[data.length - 1].id;
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      if (error.name !== 'AbortError' && active.current) setError('Vzpomínky se nepodařilo načíst.');
    } finally {
      busy.current = false;
      if (active.current) setLoading(false);
    }
  }, [profile.id, type]);
  useEffect(() => {
    active.current = true;
    loadMore();
    return () => {
      active.current = false;
      request.current?.abort();
    };
  }, [loadMore]);
  useEffect(() => {
    if (loading || !hasMore || error || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '300px' },
    );
    if (sentinel.current) observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [loading, hasMore, error, loadMore]);
  return (
    <>
      <div className={`memory-grid ${view}`}>
        {rows.map((memory) => (
          <Memory key={memory.id} memory={memory} profileSlug={profile.slug} onImageClick={onImageClick} />
        ))}
      </div>
      <div ref={sentinel} className="memory-stream-end" aria-live="polite">
        {loading && <p>Načítám vzpomínky…</p>}
        {error && <p role="alert">{error}</p>}
        {!loading && hasMore && (
          <button className="site-button quiet" onClick={loadMore}>
            <ChevronDown size={18} />
            {error ? 'Zkusit znovu' : 'Další vzpomínky'}
          </button>
        )}
        {!loading && !rows.length && !error && <p>{type === 'all' ? 'Zatím zde nejsou žádné vzpomínky.' : 'Zatím zde nejsou žádné vzpomínky tohoto typu.'}</p>}
      </div>
    </>
  );
}

export default function MemorialPage({ profileSlug: providedSlug, initialAddOpen = false }) {
  const { profileSlug: paramSlug } = useParams();
  const navigate = useNavigate();
  const contributionNotice = useLocation().state?.contributionNotice || '';
  const slug = providedSlug || paramSlug || ROMAN_SLUG;
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [type, setType] = useState('all');
  const [view, setView] = useState('grid');
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addingBusy, setAddingBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [projection, setProjection] = useState(null);
  const [projectionMode, setProjectionMode] = useState(null);
  const closeProjection = useCallback(() => setProjectionMode(null), []);
  const projectionAvailability = useCallback((enabled) => setProjection(previous => previous && previous.enabled !== enabled ? { ...previous, enabled } : previous), []);
  const closeAdd = useCallback(() => {
    setShowAdd(false);
    if (initialAddOpen) navigate(profilePath(slug), { replace: true });
  }, [initialAddOpen, navigate, slug]);
  useEffect(() => {
    const controller = new AbortController();
    setProfile(null);
    setSummary(null);
    setError('');
    setType('all');
    setProjection(null);
    setProjectionMode(null);
    setShowAdd(initialAddOpen);
    setNotice(contributionNotice);
    fetch(`/api/profiles/slug/${encodeURIComponent(slug)}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 404 ? 'Profil nebyl nalezen.' : 'Profil se nepodařilo načíst.');
        const data = normalizeProfile(await response.json());
        setProfile(data);
        document.title = `${data.name} | Vzpomínkovník`;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setError(error.message);
      });
    return () => controller.abort();
  }, [slug, initialAddOpen, contributionNotice]);
  useEffect(() => {
    if (!profile) return undefined;
    const controller = new AbortController();
    fetch(`/api/profiles/${profile.id}/summary`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        setSummary(await response.json());
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setNotice('Souhrn příspěvků se nepodařilo načíst.');
      });
    return () => controller.abort();
  }, [profile?.id, revision]);
  useEffect(() => {
    if (!profile?.id) return undefined;
    let controller;
    const check = () => {
      if (document.hidden) return;
      controller?.abort();
      controller = new AbortController();
      fetch(`/api/profiles/${profile.id}/projection-player`, { signal: controller.signal, cache: 'no-store' })
        .then(async response => {
          if (response.ok) setProjection(await response.json());
        })
        .catch(() => {});
    };
    check();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      controller?.abort();
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [profile?.id]);
  const share = async () => {
    try {
      const url = `${location.origin}${profilePath(profile.slug)}`;
      if (navigator.share) await navigator.share({ title: profile.name, url });
      else {
        await navigator.clipboard.writeText(url);
        setNotice('Odkaz na profil byl zkopírován.');
      }
    } catch (error) {
      if (error.name !== 'AbortError') setNotice('Odkaz se nepodařilo zkopírovat.');
    }
  };
  const added = (result) => {
    if (result.close) closeAdd();
    if (result.changed && !result.success) setRevision((value) => value + 1);
    if (!result.success) return;
    if (initialAddOpen) {
      navigate(profilePath(slug), { replace: true, state: { contributionNotice: result.success } });
      return;
    }
    setNotice(result.success);
    setShowAdd(false);
    setRevision((value) => value + 1);
  };
  const openAdd = () => setShowAdd(true);
  if (!profile) return <main className="site-shell site-status">{error || 'Načítám profil…'}</main>;
  const contributors = summary?.contributors || [];
  return (
    <>
      <main className="profile-page">
        <section className="profile-heading">
          <div className="site-shell profile-heading-grid">
            <ProfileAvatar profile={profile} />
            <div className="profile-copy">
              <p className="profile-label">Vzpomínáme na</p>
              <h1>{profile.name}</h1>
              {(profileDates(profile) || profile.motto) && (
                <p className="profile-dates">
                  {profileDates(profile) && <strong>{profileDates(profile)}</strong>}
                  {profile.motto && <span>{profile.motto}</span>}
                </p>
              )}
              {profile.description && <p className="profile-description">{profile.description}</p>}
              {summary && (
                <div className="profile-stats">
                  <span>{countLabel(summary.counts.all, 'vzpomínka', 'vzpomínky', 'vzpomínek')}</span>
                  {contributors.length > 0 && <a href="#lide">{countLabel(contributors.length, 'přispěvatel', 'přispěvatelé', 'přispěvatelů')}</a>}
                </div>
              )}
            </div>
            <div className="profile-commands">
              <button className="site-button" onClick={openAdd}>
                <Plus size={18} /> Přidat vzpomínku
              </button>
              <button className="site-button secondary" onClick={share}>
                <Share2 size={16} /> Sdílet profil
              </button>
              <Link className="profile-start-projection" to={`/p/${profile.slug}/prispevky`}>Moje příspěvky</Link>
              <div className="profile-projection-actions">
                <button className="profile-start-projection" onClick={() => projection?.enabled && projection.code ? setProjectionMode('live') : navigate(`/p/${encodeURIComponent(profile.slug)}/projekce`)}>
                  <Play size={15} /> {projection?.enabled ? 'Sledovat projekci' : 'Otevřít projekci'}
                </button>
                {projection?.enabled && projection.code && (
                  <button className="profile-start-projection" onClick={() => setProjectionMode('history')}>
                    <History size={15} /> Předchozí snímky
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="site-shell">
            <section className="profile-contributor-heading" id="lide" aria-labelledby="profile-contributors-title">
              <h2 id="profile-contributors-title">Přispěli</h2>
              {contributors.length ? (
                <ul className="profile-contributors">
                  {contributors.map(({ name, count }) => (
                    <li key={name}>
                      <span className="contributor-initials" aria-hidden="true">
                        {initials(name)}
                      </span>
                      <div>
                        <strong>{name}</strong>
                        <small>{countLabel(count, 'vzpomínka', 'vzpomínky', 'vzpomínek')}</small>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="profile-muted">{summary ? 'Zatím bez uvedených přispěvatelů.' : 'Načítám přispěvatele…'}</p>
              )}
            </section>
          </div>
        </section>
        <section className="profile-filterbar" id="vzpominky" aria-label="Vzpomínky">
          <div className="site-shell profile-filter-inner">
            <div className="profile-filters" role="group" aria-label="Typ vzpomínky">
              {FILTERS.map(([value, label]) => (
                <button key={value} aria-pressed={type === value} onClick={() => setType(value)}>
                  {label}
                  {summary && <span>{value === 'videos' ? (summary.counts.video || 0) + (summary.counts.youtube || 0) : summary.counts[value]}</span>}
                </button>
              ))}
            </div>
            <div className="profile-view-tools" role="group" aria-label="Zobrazení">
              <button className="site-icon" title="Karty" aria-label="Karty" aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
                <LayoutGrid size={18} />
              </button>
              <button className="site-icon" title="Seznam" aria-label="Seznam" aria-pressed={view === 'list'} onClick={() => setView('list')}>
                <List size={18} />
              </button>
            </div>
          </div>
        </section>
        <section className="site-shell profile-stream">
          {notice && (
            <p role="status" className="site-notice">
              {notice}
            </p>
          )}
          <MemoryStream key={`${profile.id}-${type}-${revision}`} profile={profile} type={type} view={view} onImageClick={setLightbox} />
        </section>
      </main>
      <SiteFooter>
        <Link to={`/p/${profile.slug}/admin`}>
          <Settings size={14} /> Správa profilu
        </Link>
      </SiteFooter>
      {showAdd && (
        <Modal title="Přidat vzpomínku" onClose={closeAdd} busy={addingBusy}>
          <AddMemory callback={added} profileId={profile.id} profileName={profile.name} profileSettings={profile.settings} onBusyChange={setAddingBusy} />
        </Modal>
      )}
      <ImageLightbox memory={lightbox} onClose={() => setLightbox(null)} />
      {projectionMode && projection?.code && (
        <Suspense fallback={<Modal title="Projekce" onClose={closeProjection}><p className="site-status">Načítám projekci…</p></Modal>}>
          <ProfileProjection profile={profile} code={projection.code} initialMode={projectionMode} onClose={closeProjection} onAvailabilityChange={projectionAvailability} />
        </Suspense>
      )}
    </>
  );
}
